import { useEffect, useRef, useState } from "react";
import { useDebug } from '@/utils/useDebugLog';

import gsap from 'gsap';
import SplitText from 'gsap/SplitText';

import className from "classnames/bind";
import styles from "./HeadlineAnimSplitLine.module.scss";
const cx = className.bind(styles);

/* Component description
 * This component animates a headline by splitting it into lines and animating them in alternately from top/bottom.
 * When the animation completes, the duplicates are removed from the DOM to save resources. */

interface HeadlineAnimSplitLineProps {
  tag?: React.ElementType;
  text: string;
  className?: string | string[];
  isCenterAlign?: boolean;
  isRevealEnabled?: boolean;
  isInView?: boolean;
  animInDelay?: number;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
}

export default function HeadlineAnimSplitLine({
  tag: Tag = "h1",
  text,
  className,
  isCenterAlign = false,
  isRevealEnabled = true,
  isInView = false,
  animInDelay = 0,
  onAnimationStart,
  onAnimationComplete,
}: HeadlineAnimSplitLineProps) {

  const { log } = useDebug({ name: 'HeadlineAnimSplitLine', bgColor: '#9B59B6', textColor: 'white', enabled: false });

  const DEBUGMODE: boolean = false;

  // States
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAnimatingComplete, setIsAnimatingComplete] = useState(false);

  // Elements
  const rootEl = useRef<HTMLDivElement>(null);
  const wrapEl = useRef<HTMLDivElement>(null);
  const animRef = useRef<HTMLElement>(null);

  // -------------------- Animation

  useEffect(() => {
    if (!isRevealEnabled || !isInView) return;
    if (!animRef.current || !wrapEl.current) return;

    // Store split and timeline refs for cleanup
    let split: SplitText | null = null;
    let tl: gsap.core.Timeline | null = null;

    const headlineDur = 1.25;
    const headlineEase = 'expo.inOut';

    // Split into lines
    split = new SplitText(animRef.current, {
      type: 'lines',
      mask: 'lines',
      linesClass: 'line'
    });

    log('Split created', { lines: split.lines.length });

    // Create timeline
    tl = gsap.timeline({
      onStart: () => {
        setIsAnimating(true);
        if (onAnimationStart) onAnimationStart();
      },
      onComplete: () => {
        if (DEBUGMODE) return;
        setIsAnimatingComplete(true);
        if (onAnimationComplete) onAnimationComplete();

        // ~ ~ ~ ~ ~ ~ ~ Was not able to get this to work right away
        // TODO: Fix later -Gabe
        // Revert split text to original state
        // split?.revert();
        // Align text to center after split has reverted
        // if (wrapEl.current) {
        //   wrapEl.current.style.textAlign = 'center';
        // }
      },
      delay: animInDelay,
    }).add('start');

    // Animate wrapper scale
    tl.fromTo(wrapEl.current, {
      scale: 0.5
    }, {
      scale: 1,
      duration: headlineDur,
      ease: headlineEase
    }, 'start');

    // Animate each line alternating from top/bottom
    split.lines.forEach((line, index) => {
      // Determine vertical offset (even/odd)
      const verticalOffset = (index % 2 === 0) ? '110%' : '-110%';

      tl.fromTo(line, {
        y: verticalOffset
      }, {
        y: 0,
        duration: headlineDur,
        ease: headlineEase
      }, 'start');
    });

    // Cleanup function
    return () => {
      if (tl) tl.kill();
      if (split) split.revert();
    };

  }, [isRevealEnabled, isInView, animInDelay, onAnimationStart, onAnimationComplete]);

  // -------------------- Render

  return (
    isRevealEnabled

    // Mode: Animation
    ? (
      <div ref={rootEl}
        className={cx("headline-anim-split-line", { centered: isCenterAlign })}
        style={{ visibility: (isRevealEnabled && !isAnimating) ? 'hidden' : 'visible' }}
      >
        <Tag
          className={cx([
            "base",
            className,
            { hidden: isRevealEnabled && isInView && !isAnimatingComplete }
          ])}
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {isRevealEnabled && !isAnimatingComplete && (
          <div className={cx("anim-wrap")} ref={wrapEl}>
            <Tag className={cx([
                "anim",
                className,
                { hidden: !isInView }
              ])}
              ref={animRef}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
        )}
      </div>
    )

    // Mode: No animation
    : (
      <Tag className={cx(["base", className])} dangerouslySetInnerHTML={{ __html: text }} />
    )
  );

}
